import axios from 'axios';
import { useAdminStore, usePortalStore } from '../stores';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Create axios instances with credentials support
const adminApi = axios.create({
  baseURL: `${API_BASE}/api/admin`,
  withCredentials: true,
});

const portalApi = axios.create({
  baseURL: `${API_BASE}/api/portal`,
  withCredentials: true,
});

const publicApi = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
});

// Add auth interceptors
adminApi.interceptors.request.use((config) => {
  const token = useAdminStore.getState().token;
  const authType = useAdminStore.getState().authType;
  
  // Only add Bearer token for JWT auth
  if (token && authType === 'jwt') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAdminStore.getState().logout();
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

portalApi.interceptors.request.use((config) => {
  const token = usePortalStore.getState().token;
  const authType = usePortalStore.getState().authType;
  
  // Only add Bearer token for JWT auth
  if (token && authType === 'jwt') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

portalApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      usePortalStore.getState().logout();
      window.location.href = '/portal/login';
    }
    return Promise.reject(error);
  }
);

// ===================== Admin API =====================

export const adminAuthApi = {
  login: (email, password) => 
    adminApi.post('/auth/login', { email, password }),
  
  getMe: () => adminApi.get('/auth/me'),
};

export const adminConfigApi = {
  getConfig: () => adminApi.get('/config'),
  updateConfig: (data) => adminApi.put('/config', data),
};

export const adminCustomersApi = {
  overview: () => adminApi.get('/customers/overview'),
  list: (params) => adminApi.get('/customers', { params }),
  detail: (id, days = 90) => adminApi.get(`/customers/${id}`, { params: { days } }),
  conversations: (id, params) => adminApi.get(`/customers/${id}/conversations`, { params }),
  conversation: (id, convId) => adminApi.get(`/customers/${id}/conversations/${convId}`),
  invoicePdf: (id, invoiceId) => adminApi.get(`/customers/${id}/invoices/${invoiceId}/pdf`, { responseType: 'blob' }),
  qaInsights: (params) => adminApi.get('/customers/insights/qa', { params }),
  feedback: (params) => adminApi.get('/customers/insights/feedback', { params }),
  exportCsv: () => adminApi.get('/customers/export.csv', { responseType: 'blob' }),
};

export const adminFoundationApi = {
  listKBs: () => adminApi.get('/foundation-kb'),
  createKB: (data) => adminApi.post('/foundation-kb', data),
  listChunks: (fkbId) => adminApi.get(`/foundation-chunks/${fkbId}`),
  createChunk: (data) => adminApi.post('/foundation-chunks', data),
  deleteChunk: (chunkId) => adminApi.delete(`/foundation-chunks/${chunkId}`),
  getInsights: (kbId) => adminApi.get(`/foundation-kb/${kbId}/insights`),
  trainFromUrl: (kbId, data) => adminApi.post(`/foundation-kb/${kbId}/train-url`, data),
  trainFromChat: (kbId, data) => adminApi.post(`/foundation-kb/${kbId}/train-chat`, data),
};

export const adminTenantsApi = {
  list: () => adminApi.get('/tenants'),
  get: (id) => adminApi.get(`/tenants/${id}`),
  create: (data) => adminApi.post('/tenants', data),
  update: (id, data) => adminApi.put(`/tenants/${id}`, data),
  delete: (id) => adminApi.delete(`/tenants/${id}`),
  listUsers: (tenantId) => adminApi.get(`/tenants/${tenantId}/users`),
  createUser: (tenantId, data) => adminApi.post(`/tenants/${tenantId}/users`, data),
  deleteUser: (tenantId, userId) => adminApi.delete(`/tenants/${tenantId}/users/${userId}`),
  setDomain: (tenantId, domain) => adminApi.put(`/tenants/${tenantId}/domain`, { custom_domain: domain }),
  removeDomain: (tenantId) => adminApi.delete(`/tenants/${tenantId}/domain`),
  rotateShareCode: (tenantId) => adminApi.post(`/tenants/${tenantId}/rotate-share-code`),
  listDomains: () => adminApi.get('/domains'),
};

export const adminDashboardApi = {
  getStats:      () => adminApi.get('/dashboard/stats'),
  getSparklines: (days = 14) => adminApi.get('/dashboard/sparklines', { params: { days } }),
};

// ===================== Portal API =====================

export const portalAuthApi = {
  login: (email, password) => 
    publicApi.post(`/portal/auth/login`, { email, password }),
  
  register: (data) => 
    publicApi.post(`/portal/auth/register`, data),
  
  getMe: () => portalApi.get('/auth/me'),
};

export const portalKnowledgeApi = {
  list: () => portalApi.get('/knowledge'),
  create: (data) => portalApi.post('/knowledge', data),
  listChunks: (kbId) => portalApi.get(`/knowledge/${kbId}/chunks`),
  addChunk: (kbId, data) => portalApi.post(`/knowledge/${kbId}/chunks`, data),
  delete: (kbId) => portalApi.delete(`/knowledge/${kbId}`),
  // Multipart file upload — server enqueues a background ingestion job
  // and returns `{job_id}` immediately (sidesteps ingress timeouts).
  upload: (kbId, file, onUploadProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return portalApi.post(`/knowledge/${kbId}/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 0,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });
  },
  // Poll a single ingestion job's status / progress.
  getIngestionJob: (jobId) => portalApi.get(`/knowledge/ingestion-jobs/${jobId}`),
  // List ingestion jobs — used to render an "Ingesting…" pill on KB cards.
  listIngestionJobs: ({ kbId, onlyActive } = {}) => {
    const params = {};
    if (kbId) params.kb_id = kbId;
    if (onlyActive) params.only_active = true;
    return portalApi.get('/knowledge/ingestion-jobs', { params });
  },
};

export const portalConfigApi = {
  get: () => portalApi.get('/config'),
  update: (data) => portalApi.put('/config', data),
};

export const portalApiKeysApi = {
  list: () => portalApi.get('/api-keys'),
  create: (data) => portalApi.post('/api-keys', data),
  revoke: (keyId) => portalApi.delete(`/api-keys/${keyId}`),
};

export const portalDashboardApi = {
  getStats: () => portalApi.get('/dashboard/stats'),
};

export const portalBillingAlertsApi = {
  list: () => portalApi.get('/billing/alerts'),
  saveConfig: (data) => portalApi.post('/billing/alerts/config', data),
  check: () => portalApi.post('/billing/alerts/check'),
};

export const portalConversationsApi = {
  list: (params) => portalApi.get('/conversations', { params }),
  get: (id) => portalApi.get(`/conversations/${id}`),
  rename: (id, title) => portalApi.patch(`/conversations/${id}`, { title }),
  remove: (id) => portalApi.delete(`/conversations/${id}`),
  pinFact: (id, text) => portalApi.post(`/conversations/${id}/pins`, { text }),
  unpinFact: (id, pinId) => portalApi.delete(`/conversations/${id}/pins/${pinId}`),
  feedback: (id, payload) => portalApi.post(`/conversations/${id}/feedback`, payload),
  // Create a pre-populated conversation. Used by the APS Asset drawer's
  // "Send to Knowledge Chat" button so a generated BoQ / schedule /
  // clash report lands directly in the main chat for further iteration.
  seed: (body) => portalApi.post('/conversations/seed', body),
};

export const portalToolsApi = {
  list: () => portalApi.get('/tools'),
  create: (data) => portalApi.post('/tools', data),
  update: (id, data) => portalApi.put(`/tools/${id}`, data),
  delete: (id) => portalApi.delete(`/tools/${id}`),
};

export const portalDocumentsApi = {
  // Start async generation — returns { job_id, status, format } immediately
  start: (body) => portalApi.post('/documents/generate', body),
  // Poll status — returns { status, title?, payload?, download_ready?, error? }
  status: (jobId) => portalApi.get(`/documents/jobs/${jobId}`),
  // List in-flight jobs for the current user (for tab-switch resume)
  listActive: () => portalApi.get('/documents/jobs'),
  // User-requested cancel — marks job cancelled server-side
  cancel: (jobId) => portalApi.post(`/documents/jobs/${jobId}/cancel`),
  // Download the produced file — returns a Blob
  download: (jobId) => portalApi.get(`/documents/jobs/${jobId}/download`, { responseType: 'blob' }),
  // Legacy — list supported formats
  formats: () => portalApi.get('/documents/formats'),
  // Activity Timeline — every docgen job in this conversation (audit log)
  historyForConversation: (conversationId) =>
    portalApi.get(`/conversations/${conversationId}/docgen-history`),
};

// ── APS (Autodesk Platform Services) — 3D asset upload + viewer + AI ───────
export const portalAssetsApi = {
  // Short-lived viewables:read token used by the embedded APS Viewer
  viewerToken: () => portalApi.get('/assets/viewer-token'),
  // Upload .rvt/.ifc/.dwg/.nwd/.obj/etc. — backend streams body to disk
  // then background-tasks the OSS upload + translation, so this request
  // only carries the file bytes (no idle time). No artificial size cap.
  upload: (file, onUploadProgress) => {
    const form = new FormData();
    form.append('file', file);
    return portalApi.post('/assets/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 0, // disable axios timeout — uploads of multi-GB files can take a while
    });
  },
  list: () => portalApi.get('/assets'),
  get: (id) => portalApi.get(`/assets/${id}`),
  refresh: (id) => portalApi.post(`/assets/${id}/refresh`),
  remove: (id) => portalApi.delete(`/assets/${id}`),
  // Properties Browser — ranked unique property keys with sample values.
  // Powers the "Map by:" attribute picker in the AI rail.
  propertyKeys: (id) => portalApi.get(`/assets/${id}/property-keys`),
  // Unique values for a given property key — powers the Prescope value drill-down.
  propertyValues: (id, key, opts = {}) => portalApi.get(
    `/assets/${id}/property-values`,
    { params: { key, search: opts.search || '', limit: opts.limit || 500 } },
  ),
  // Persisted analysis history — list / load / delete past runs.
  listAnalyses: (id) => portalApi.get(`/assets/${id}/analyses`),
  getAnalysis: (id, kind, sessionId = 'default') =>
    portalApi.get(`/assets/${id}/analyses/${kind}/${sessionId}`),
  deleteAnalysis: (id, kind, sessionId = 'default') =>
    portalApi.delete(`/assets/${id}/analyses/${kind}/${sessionId}`),
  nameIndex: (id) => portalApi.get(`/assets/${id}/name-index`),
  // Aggregated credit consumption across all runs for this asset.
  // Sourced from credit_transactions so it matches the wallet ledger.
  creditSummary: (id) => portalApi.get(`/assets/${id}/credit-summary`),
  // Engine Optimizer — recommends a cheaper engine for routine work
  // by re-projecting actual consumption against alternative multipliers.
  engineAdvice: (id) => portalApi.get(`/assets/${id}/engine-advice`),
  clashRecords: (id) => portalApi.get(`/assets/${id}/clash-records`),
  upsertClashRecord: (id, clashId, body) =>
    portalApi.put(`/assets/${id}/clash-records/${encodeURIComponent(clashId)}`, body),
};

export const portalTeamApi = {
  members: () => portalApi.get('/team-members'),
};

export const portalChatApi = {
  sendMessage: async (messages, conversationId, onChunk) => {
    const token = usePortalStore.getState().token;
    
    const response = await fetch(`${API_BASE}/api/portal/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        messages,
        stream: true,
        conversation_id: conversationId,
      }),
    });
    
    if (!response.ok) {
      throw new Error('Chat request failed');
    }
    
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let result = { conversationId: null, usage: null };
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'content') {
              onChunk(data.text);
            } else if (data.type === 'done') {
              result = { conversationId: data.conversation_id, usage: data.usage };
            } else if (data.type === 'error') {
              throw new Error(data.message);
            }
          } catch (e) {
            // Skip invalid JSON
          }
        }
      }
    }
    
    return result;
  },
};

// Health check
export const healthApi = {
  check: () => publicApi.get('/health'),
};

// ============================================================
// Super-Admin · Organization Management (admin scope)
// ============================================================
export const adminOrgsApi = {
  create: (data) => adminApi.post('/orgs', data),
  grant: (tenantId, credits, note) =>
    adminApi.post(`/orgs/${tenantId}/grant`, { credits, note }),
  addAdmin: (tenantId, email, sendEmail = true) =>
    adminApi.post(`/orgs/${tenantId}/admins`, { email, send_email: sendEmail }),
};

// ============================================================
// Portal · Per-User Credit Allocation (org admin scope)
// ============================================================
export const portalTeamAllocApi = {
  list: () => portalApi.get('/team/allocations'),
  allocate: (userId, credits) =>
    portalApi.post(`/team/${userId}/allocate`, { credits }),
};

// Member-facing self view
export const portalMeApi = {
  allocation: (days = 14) => portalApi.get(`/me/allocation?days=${days}`),
};

// ===================== Ingestion API =====================

export const ingestionApi = {
  uploadDocument: (formData) => publicApi.post('/ingestion/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }),
  uploadDocumentAsync: (formData) => publicApi.post('/ingestion/upload/async', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }),
  getProgress: (taskId) => publicApi.get(`/ingestion/progress/${taskId}`),
  searchKnowledge: (data) => publicApi.post('/ingestion/search', data),
  listFiles: (kbId, kbType = 'foundation') =>
    publicApi.get(`/ingestion/files/${kbId}`, { params: { kb_type: kbType } }),
};

// ===================== Orchestration API =====================

export const orchestrationApi = {
  chat: (data) => publicApi.post('/orchestrate/chat', data),
  chatStream: (data) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/orchestrate/chat/stream`;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    });
  },
  health: () => publicApi.get('/orchestrate/health'),
};

// ===================== Billing API =====================

export const billingApi = {
  getPlans: () => publicApi.get('/billing/plans'),
  getCurrentBilling: () => portalApi.get('/billing/current'),
  getUsageDetails: (period = '7d') => portalApi.get(`/billing/usage-details?period=${period}`),
  getInvoices: () => portalApi.get('/billing/invoices'),
  createOrder: (data) => portalApi.post('/billing/create-order', data),
  verifyPayment: (data) => portalApi.post('/billing/verify-payment', data),
  activateDemoPlan: (data) => portalApi.post('/billing/activate-free', data),
};

// ===================== Analytics API =====================

export const adminAnalyticsApi = {
  getTimeseries: (range = '30d') => adminApi.get('/analytics/timeseries', { params: { range } }),
  getTopTenants: (limit = 10) => adminApi.get('/analytics/top-tenants', { params: { limit } }),
  getModelDistribution: () => adminApi.get('/analytics/model-distribution'),
};

export const portalAnalyticsApi = {
  getTimeseries: (range = '30d') => portalApi.get('/analytics/timeseries', { params: { range } }),
  getApiKeysUsage: () => portalApi.get('/analytics/api-keys-usage'),
};

// ===================== Audit Logs API =====================

export const adminAuditApi = {
  getLogs: (params) => adminApi.get('/audit-logs', { params }),
};

export const portalAuditApi = {
  getLogs: (params) => portalApi.get('/audit-logs', { params }),
};

// ===================== Export API =====================

export const portalExportApi = {
  exportConversation: (convId, format = 'json') => portalApi.get(`/export/conversation/${convId}`, { params: { format }, responseType: 'blob' }),
  exportAll: (format = 'json') => portalApi.get('/export/conversations', { params: { format }, responseType: 'blob' }),
};

// ===================== Branding API =====================

export const portalBrandingApi = {
  get: () => portalApi.get('/branding'),
  update: (data) => portalApi.put('/branding', data),
};
